import { useMemo } from 'react';

import { useBeneficiariesActions } from '@/entities/beneficiary';
import { getPersonFullName } from '@/shared/helpers/names';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

export type PidCreateData = Omit<PersonalIdentifierDocumentDto, 'id' | 'type'>;
export type PidUpdateData = Omit<PersonalIdentifierDocumentDto, 'type'>;

export const useActions = () => {
  const keyPair = useKeyPairStore((s) => s.keyPair);
  const pidsActions = useSjActions('personalIdentifiersDocuments');
  const assetsActions = useSjActions('assets');
  const privateDocumentsActions = useSjActions('privateDocuments');
  const { findById } = useBeneficiariesActions();
  const { create: pidsCreate, update: pidsUpdate, ...pidsActionsRest } = pidsActions;

  return useMemo(() => {
    const create = async (data: PidCreateData): Promise<PersonalIdentifierDocumentDto | null> => {
      if (!keyPair?.publicKey) {
        return null;
      }

      const res = await pidsCreate(
        {
          id: pidsActionsRest.generateId(),
          type: DocumentType.PERSONAL_IDENTIFIERS,
          ...data,
        },
        [keyPair.publicKey],
      );

      return res;
    };

    const update = async (data: PidUpdateData): Promise<PersonalIdentifierDocumentDto | null> => {
      const dto: PersonalIdentifierDocumentDto = {
        type: DocumentType.PERSONAL_IDENTIFIERS,
        ...data,
      };

      await pidsUpdate(dto);

      return dto;
    };

    const getAssociatedBeneficiary = (item: PersonalIdentifierDocumentDto) => {
      const beneficiaryId = item.beneficiaryId;
      const isSelf = beneficiaryId === 'me';
      const beneficiaryData = beneficiaryId ? findById(beneficiaryId) : undefined;
      const beneficiaryName = isSelf
        ? 'Me'
        : beneficiaryData
        ? getPersonFullName(beneficiaryData, 'display')
        : '-';

      return {
        associatedWith: beneficiaryName,
        beneficiary: beneficiaryData,
      };
    };

    const getLinkedAssets = (item: PersonalIdentifierDocumentDto) => {
      const allAssets = assetsActions?.findMany(() => true) || [];

      return allAssets.filter((asset) => {
        if (asset.ownerDocument?.includes(item.id)) {
          return true;
        }

        if (asset.coOwners) {
          return asset.coOwners.some((coOwner) => coOwner.coOwnerDocument?.includes(item.id));
        }

        return false;
      });
    };

    const getLinkedPrivateDocuments = (item: PersonalIdentifierDocumentDto) => {
      const allDocs = privateDocumentsActions?.findMany(() => true) || [];
      return allDocs.filter((doc) => doc.signatories?.includes(item.id));
    };

    return {
      create,
      update,
      getAssociatedBeneficiary,
      getLinkedAssets,
      getLinkedPrivateDocuments,
      ...pidsActionsRest,
    };
  }, [keyPair, pidsActions]);
};
